import React, { createContext, FC, useEffect, useState, useContext } from "react";
import { cloneDeep } from "lodash";
import { toast } from "react-toastify";
import { BenchmarkingQHPService } from "@app/overviews/network-benchmarking-dashboard";
import { BenchmarkingFiltersContext } from "@app/overviews/network-benchmarking-dashboard/filters";
import {
  BenchmarkingCounty,
  BenchmarkingLineOfBusinessEnum,
  BenchmarkingState,
} from "@app/overviews/network-benchmarking-dashboard/filters/shared";
import {
  QHPNetwork,
  QHPNetworkTypeEnum,
  QHPParentOrganization,
  QHPParentOrganizationSortingTypeEnum,
  QHPPrimaryFiltersPayload,
  QHPStarRatingTypeEnum,
} from "@app/overviews/network-benchmarking-dashboard/marketplace/filters";
import { DropdownItem } from "@app/shared/checkbox-dropdown";
import { UserContext } from "@app/user";

export interface QHPFiltersContextValues {
  applyingServiceArea: QHPNetwork;
  counties: DropdownItem<BenchmarkingCounty>[];
  mappedStarRatings: DropdownItem<QHPStarRatingTypeEnum>[];
  networkFiltersLoading: boolean;
  onApplyPrimaryFiltersClick: () => void;
  onApplyServiceAreaClick: (countyIds: number[], network: QHPNetwork) => void;
  onClearNetworkFiltersClick: () => void;
  onClearPrimaryFiltersClick: () => void;
  parentOrganizations: QHPParentOrganization[];
  planTypes: DropdownItem<string>[];
  primaryFiltersLoading: boolean;
  selectedCounties: DropdownItem<BenchmarkingCounty>[];
  selectedNetworkType: QHPNetworkTypeEnum;
  selectedParentOrganizations: QHPParentOrganization[];
  selectedPlanTypes: DropdownItem<string>[];
  selectedSortType: QHPParentOrganizationSortingTypeEnum;
  selectedStarRatings: DropdownItem<QHPStarRatingTypeEnum>[];
  selectedState: BenchmarkingState;
  setSelectedCounties: (selectedCounties: DropdownItem<BenchmarkingCounty>[]) => void;
  setSelectedNetworkType: (selectedPlanOption: QHPNetworkTypeEnum) => void;
  setSelectedParentOrganizations: (selectedParentOrganizations: QHPParentOrganization[]) => void;
  setSelectedPlanTypes: (selectedPlanTypes: DropdownItem<string>[]) => void;
  setSelectedSortType: (selectedSortType: QHPParentOrganizationSortingTypeEnum) => void;
  setSelectedStarRatings: (selectedStarRatings: DropdownItem<QHPStarRatingTypeEnum>[]) => void;
  setSelectedState: (selectedState: BenchmarkingState) => void;
}

const initialValues: QHPFiltersContextValues = {
  applyingServiceArea: null,
  counties: [],
  mappedStarRatings: [],
  networkFiltersLoading: false,
  onApplyPrimaryFiltersClick: () => null,
  onApplyServiceAreaClick: () => null,
  onClearNetworkFiltersClick: () => null,
  onClearPrimaryFiltersClick: () => null,
  parentOrganizations: [],
  planTypes: [],
  primaryFiltersLoading: false,
  selectedCounties: [],
  selectedNetworkType: null,
  selectedParentOrganizations: [],
  selectedPlanTypes: [],
  selectedSortType: null,
  selectedStarRatings: [],
  selectedState: null,
  setSelectedCounties: () => null,
  setSelectedNetworkType: () => null,
  setSelectedParentOrganizations: () => null,
  setSelectedPlanTypes: () => null,
  setSelectedSortType: () => null,
  setSelectedStarRatings: () => null,
  setSelectedState: () => null,
}

export const QHPFiltersContext = createContext(initialValues);

const QHPFiltersContextProvider: FC<{ children: React.ReactNode }> = (props: { children: React.ReactNode }) => {
  const [applyingServiceArea, setApplyingServiceArea] = useState<QHPNetwork>(null);
  const [counties, setCounties] = useState<DropdownItem<BenchmarkingCounty>[]>(initialValues.counties);
  const [planTypes, setPlanTypes] = useState<DropdownItem<string>[]>(initialValues.planTypes);
  const [mappedStarRatings, setMappedStarRatings] = useState<DropdownItem<QHPStarRatingTypeEnum>[]>(initialValues.mappedStarRatings);
  const [networkFiltersLoading, setNetworkFiltersLoading] = useState<boolean>(initialValues.networkFiltersLoading);
  const [parentOrganizations, setParentOrganizations] = useState<QHPParentOrganization[]>(initialValues.parentOrganizations);
  const [primaryFiltersLoading, setPrimaryFiltersLoading] = useState<boolean>(initialValues.primaryFiltersLoading);
  const [selectedCounties, setSelectedCounties] = useState<DropdownItem<BenchmarkingCounty>[]>(initialValues.selectedCounties);
  const [selectedParentOrganizations, setSelectedParentOrganizations] = useState<QHPParentOrganization[]>([]);
  const [selectedSortType, setSelectedSortType] = useState<QHPParentOrganizationSortingTypeEnum>(null);
  const [selectedStarRatings, setSelectedStarRatings] = useState<DropdownItem<QHPStarRatingTypeEnum>[]>(initialValues.selectedStarRatings);
  const [selectedState, setSelectedState] = useState<BenchmarkingState>(initialValues.selectedState);
  const [selectedNetworkType, setSelectedNetworkType] = useState<QHPNetworkTypeEnum>(initialValues.selectedNetworkType);
  const [selectedPlanTypes, setSelectedPlanTypes] = useState<DropdownItem<string>[]>(initialValues.selectedPlanTypes);
  
  const userContext = useContext(UserContext);
  const benchmarkingFiltersContext = useContext(BenchmarkingFiltersContext);

  const benchmarkingQHPService = new BenchmarkingQHPService();

  const starRatings: DropdownItem<QHPStarRatingTypeEnum>[] = Object.values(QHPStarRatingTypeEnum).map(x => {
    return { key: x, name: x }
  });

  useEffect(() => {
    if (benchmarkingFiltersContext.selectedLineOfBusiness === BenchmarkingLineOfBusinessEnum.Marketplace) {
      setPrimaryFiltersLoading(true);
      benchmarkingFiltersContext.setDisplayedPrimaryFilters(null);

      benchmarkingQHPService.getBenchmarkingPrimaryFilterData(userContext.clientId).then((data) => {
        if (!!data) {
          benchmarkingFiltersContext.setSpecialties(data.specialties);
          benchmarkingFiltersContext.setStates(data.states);
          setSelectedState(null);
          setCounties([]);
          setSelectedCounties([]);
          setMappedStarRatings(cloneDeep(starRatings));
          setSelectedStarRatings(cloneDeep(starRatings));
          setSelectedNetworkType(QHPNetworkTypeEnum.All);
          
          const planTypes = data.planTypes.map(x => {return { key: x, name: x }});
          setPlanTypes(cloneDeep(planTypes));
          setSelectedPlanTypes(cloneDeep(planTypes));
        }

        setPrimaryFiltersLoading(false);
      });
    }
  }, [benchmarkingFiltersContext.selectedLineOfBusiness]);

  useEffect(() => {
    if (applyingServiceArea) {
      onApplyPrimaryFiltersClick();
    } else {
      if (!!benchmarkingFiltersContext.displayedPrimaryFilters || !!benchmarkingFiltersContext.displayedNetworkFilters?.length) {
        // on state or county change, clear all displayed filters and selections for both filters
        benchmarkingFiltersContext.setDisplayedPrimaryFilters(null);
        onClearNetworkFiltersClick();
      }
    }
  }, [selectedState, selectedCounties, selectedStarRatings, selectedNetworkType, selectedPlanTypes]);

  useEffect(() => {
    if (!!benchmarkingFiltersContext.displayedNetworkFilters?.length) {
      // on network change, clear all displayed network filters
      benchmarkingFiltersContext.setDisplayedNetworkFilters([]);
    }
  }, [benchmarkingFiltersContext.selectedNetworks, selectedParentOrganizations]);

  useEffect(() => {
    if (!!selectedState) {
      const mappedCounties: DropdownItem<BenchmarkingCounty>[] = selectedState.counties.map(x => {
        return { key: x, name: x.countyName }
      });

      setCounties(mappedCounties)
      setSelectedCounties(mappedCounties);
    }
  }, [selectedState]);

  const onApplyServiceAreaClick = (countyIds: number[], network: QHPNetwork): void => {
    const newSelectedCounties: DropdownItem<BenchmarkingCounty>[] = counties.filter(x => countyIds.includes(x.key.countyId));
    setSelectedCounties(newSelectedCounties)
    setApplyingServiceArea(network);
  }

  const onApplyPrimaryFiltersClick = (): void => {
    setNetworkFiltersLoading(true);

    const formatPayload = (payload: QHPPrimaryFiltersPayload) => {
      if (payload.starRatings.length === mappedStarRatings.length) {
        payload.starRatings = [];
      }
    }

    const primaryFilters: QHPPrimaryFiltersPayload = {
      countyIds: selectedCounties.map(x => x.key.countyId),
      lineOfBusinessId: benchmarkingFiltersContext.selectedLineOfBusiness,
      networkType: selectedNetworkType,
      planTypes: selectedPlanTypes.map(x => x.key),
      starRatings: selectedStarRatings.map(x => x.key),
      stateAbbreviation: selectedState.stateAbbreviation
    };

    benchmarkingFiltersContext.setDisplayedPrimaryFilters(cloneDeep(primaryFilters));

    formatPayload(primaryFilters);

    benchmarkingQHPService.getBenchmarkingNetworkData(primaryFilters).then((data) => {
      if (!!data) {
        const yourNetworks: QHPParentOrganization = {
          hContracts: data.yourNetworks,
          issuerIds: [data.yourNetworks[0]?.issuerId],
          parentOrganizationName: "Your QES Networks",
        };

        for (const parentOrg of data.comparisonNetworks) {
          for (const contract of parentOrg.hContracts) {
            contract.parentOrganization = parentOrg.parentOrganizationName;
          }
        }

        let newParentOrganizations = [yourNetworks];
        newParentOrganizations = newParentOrganizations.concat(data.comparisonNetworks);

        for (const parentOrg of newParentOrganizations) {
          for (const contract of parentOrg.hContracts) {
            contract.parentOrganization = parentOrg.parentOrganizationName;
          }
        }

        setParentOrganizations(newParentOrganizations);

        const newSelectedParentOrganizations: QHPParentOrganization[] = [cloneDeep(yourNetworks)];

        if (applyingServiceArea) {
          toast("Service Area Updated", {
            containerId: "z-index-9999",
            type: "success",
          });

          if (applyingServiceArea.disabled) {
            benchmarkingFiltersContext.setSelectedNetworks([]);
          } else {
            // when applying the service area, select the parent organization tile of the contract the service area was applied for if the contract is not disabled
            const parentOrganization: QHPParentOrganization = newParentOrganizations.find(x => x.parentOrganizationName === applyingServiceArea.parentOrganization);

            if (parentOrganization.parentOrganizationName !== yourNetworks.parentOrganizationName) {
              newSelectedParentOrganizations.push(parentOrganization);
            }

            benchmarkingFiltersContext.setSelectedNetworks([applyingServiceArea]);
          }

          setApplyingServiceArea(null);
        }

        setSelectedParentOrganizations(newSelectedParentOrganizations);
      }

      setNetworkFiltersLoading(false);
    });
  }

  const onClearPrimaryFiltersClick = (): void => {
    setSelectedCounties([]);
    setCounties([]);
    setSelectedState(null);
    benchmarkingFiltersContext.setDisplayedPrimaryFilters(null);
    setSelectedStarRatings(cloneDeep(starRatings));
    setSelectedNetworkType(QHPNetworkTypeEnum.All);
    setSelectedPlanTypes(cloneDeep(planTypes));
    // if resetting the primary filters,
    // also reset the network filters that are based off of the primary filters
    onClearNetworkFiltersClick();
  }

  const onClearNetworkFiltersClick = (): void => {
    setSelectedParentOrganizations([]);
    benchmarkingFiltersContext.setSelectedNetworks([]);
    benchmarkingFiltersContext.setDisplayedNetworkFilters([]);
  }

  return (
    <QHPFiltersContext.Provider
      value={{
        applyingServiceArea,
        counties,
        mappedStarRatings,
        networkFiltersLoading,
        onApplyPrimaryFiltersClick,
        onApplyServiceAreaClick,
        onClearNetworkFiltersClick,
        onClearPrimaryFiltersClick,
        parentOrganizations,
        planTypes,
        primaryFiltersLoading,
        selectedCounties,
        selectedNetworkType,
        selectedParentOrganizations,
        selectedPlanTypes,
        selectedSortType,
        selectedStarRatings,
        selectedState,
        setSelectedCounties,
        setSelectedNetworkType,
        setSelectedParentOrganizations,
        setSelectedPlanTypes,
        setSelectedSortType,
        setSelectedStarRatings,
        setSelectedState,
      }}>
      {props.children}
    </QHPFiltersContext.Provider>
  )
}

export default QHPFiltersContextProvider;